/**
 * Derived collection + habit data for the active profile. Everything is computed from the journal,
 * progress and the synced collection state (`progress.collect`), memoised so selectors stay stable.
 *
 * Living drawings: the creature book shows the child's living cut-outs from the living feature's
 * store (`components/living/creatures`, `useCreatures()`, persisted as `sketchling-creatures-v1`).
 */
import { useEffect, useMemo } from 'react';
import { albumTray, creatureBook, creatureSubjects, habitDay, habitDaysBetween, habitGarden, levelFor, setTodayPick, stickerAlbum, todaysDrawing, weekRhythm, type CollectStats, type CreatureSubject, type DailyCandidate, type DrawnRecord, type Drawing, type PlaygroundItem } from '@sketchling/core';
import { appNow, useAccess, useActiveProfile, useApp, useJournal, useProgress, type JournalItem } from '../../state/store';
import { IX, WORLD_PLANS, worldDoodles, worldDrawings } from '../../lib/content';
import { worldOf } from '../../art/worlds';
import { useCollectProfile } from './collectStore';
import { useCreatures, type CreatureMeta } from '../../components/living/creatures';

/** A living creature (cut-out of the child's drawing) from the living feature's store. */
export type LivingCreature = CreatureMeta;

/** Every collectible subject (path + off-path lessons + doodles, by owning world), present in the content. */
let SUBJECTS: CreatureSubject[] | null = null;
export function allSubjects(): CreatureSubject[] {
  SUBJECTS ??= creatureSubjects(
    WORLD_PLANS.map((w) => ({ id: w.id, plan: w.plan, drawings: [...worldDrawings(w.id), ...worldDoodles(w.id)] })),
  ).filter((s) => IX.drawings.has(s.drawingId));
  return SUBJECTS;
}

/** Playground item (and so the lesson) that plays a drawing. */
const ITEM_BY_DRAWING = new Map<string, PlaygroundItem>();
export function itemForDrawing(drawingId: string): PlaygroundItem | undefined {
  if (!ITEM_BY_DRAWING.size) for (const i of IX.pack.playground.items) ITEM_BY_DRAWING.set(i.drawingId, i);
  return ITEM_BY_DRAWING.get(drawingId);
}

/** Lesson route for a drawing (back to `from` afterwards). */
export const lessonRoute = (item: PlaygroundItem, from: string) => `/lesson/${item.miniUnitId}?pg=${item.id}&from=${encodeURIComponent(from)}`;

/** Drawings that count for collections and the weekly rhythm (calibration scribbles do not). */
export const countsAsDrawing = (j: JournalItem) => j.source !== 'calibration';
export const dayOf = (iso: string) => habitDay(new Date(iso));

export function useCollectData() {
  const journal = useJournal();
  const p = useProgress();
  const col = useCollectProfile();
  // Newest first (living store), so the newest creature of a subject wins its card.
  const creatures = useCreatures();
  const today = habitDay(appNow());
  return useMemo(() => {
    const subjects = allSubjects();
    const drawings = journal.filter(countsAsDrawing);
    const records: DrawnRecord[] = drawings.filter((j) => j.drawingId).map((j) => ({ drawingId: j.drawingId!, at: j.createdAt, journalId: j.id }));
    const inJournal = new Set(records.map((r) => r.drawingId));
    for (const id of p.completedPlaygroundItemIds) {
      const d = IX.pack.playground.items.find((i) => i.id === id)?.drawingId;
      if (d && !inJournal.has(d)) records.push({ drawingId: d, at: p.updatedAt });
    }
    const living = new Map<string, LivingCreature>();
    for (const c of creatures) if (c.drawingId && !living.has(c.drawingId)) living.set(c.drawingId, c);
    for (const c of creatures) if (c.drawingId && !records.some((r) => r.drawingId === c.drawingId)) records.push({ drawingId: c.drawingId, at: p.updatedAt, journalId: c.journalId });
    const book = creatureBook(subjects, records, col.opened);
    const days = drawings.map((j) => dayOf(j.createdAt));
    const garden = habitGarden(days, today, col.goal, 8);
    const rhythm = weekRhythm(days, today, col.goal);
    const stats: CollectStats = { drawings: drawings.length, level: levelFor(p.totalSparks).level, blooms: garden.blooms, creatures: book.found };
    const drawnIds = new Set(records.map((r) => r.drawingId));
    const album = stickerAlbum(subjects, drawnIds, stats, col.placed);
    const tray = albumTray(album);
    const fresh = book.pages.reduce((n, pg) => n + pg.cards.filter((c) => c.fresh).length, 0);
    return { today, subjects, drawings, book, album, tray, fresh, garden, rhythm, stats, living, days };
  }, [journal, p.completedPlaygroundItemIds, p.updatedAt, p.totalSparks, col, creatures, today]);
}

/* -------------------------------- Today's drawing -------------------------------- */

let CANDIDATES: DailyCandidate[] | null = null;
function candidates(): DailyCandidate[] {
  CANDIDATES ??= IX.pack.playground.items.flatMap((i) => {
    const d = IX.drawings.get(i.drawingId);
    if (!d || d.id.startsWith('part-')) return [];
    return [{ drawingId: d.id, itemId: i.id, category: d.category, world: worldOf(d), minutes: d.minutes, difficulty: d.difficulty, free: !!i.free }];
  });
  return CANDIDATES;
}

export interface TodayInfo {
  day: string;
  drawing: Drawing;
  item: PlaygroundItem;
  minutes: number;
  reason: 'favourite' | 'stretch' | 'fresh' | 'again';
  /** Today's journal entry of the pick (done today). */
  doneEntry: JournalItem | null;
}

export function useToday(): TodayInfo | null {
  const journal = useJournal();
  const p = useProgress();
  const profile = useActiveProfile();
  const access = useAccess();
  const day = habitDay(appNow());
  const info = useMemo(() => {
    if (!profile) return null;
    // Last day each subject was drawn BEFORE today, so the pick stays the same after drawing it.
    const lastDrawn: Record<string, string> = {};
    for (const j of journal) {
      if (!j.drawingId || !countsAsDrawing(j)) continue;
      const d = dayOf(j.createdAt);
      if (d >= day) continue;
      if (!lastDrawn[j.drawingId] || lastDrawn[j.drawingId]! < d) lastDrawn[j.drawingId] = d;
    }
    const pick = todaysDrawing({
      day,
      seed: profile.id,
      candidates: candidates(),
      level: levelFor(p.totalSparks).level,
      favourites: profile.favoriteSubjects,
      lastDrawn,
      premium: access.premium || access.unlockAll,
    });
    if (!pick) return null;
    const drawing = IX.drawings.get(pick.candidate.drawingId);
    const item = itemForDrawing(pick.candidate.drawingId);
    if (!drawing || !item) return null;
    const doneEntry = journal.find((j) => j.drawingId === drawing.id && dayOf(j.createdAt) === day) ?? null;
    return { day, drawing, item, minutes: drawing.minutes, reason: pick.reason, doneEntry };
  }, [journal, p.totalSparks, profile, access.premium, access.unlockAll, day]);
  // Remember the pick in synced progress so the server's reminder push names the same drawing.
  const pid = profile?.id;
  const pickId = info?.drawing.id;
  useEffect(() => {
    if (pid && pickId) useApp.getState().updateCollect(pid, (c) => setTodayPick(c, day, pickId));
  }, [pid, pickId, day]);
  return info;
}

/** Days since the last drawing (null = never). */
export function daysSinceLast(days: readonly string[], today: string): number | null {
  if (!days.length) return null;
  const last = [...days].sort().pop()!;
  return Math.max(0, habitDaysBetween(last, today));
}
